import { canPerformStaffWork } from '../lib/staffWorkOwnership.js';

const normalizePositiveId = value => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
};

const normalizeWorkflowStage = value =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const normalizeEligibility = value => String(value || '').trim().toLowerCase();

const isSubmitterCorrectionStage = stage =>
  stage === 'returned_to_submitter' || stage === 'withdrawn';

export const canPreserveReturnedAssessmentEligibility = ({
  reviewWorkflow,
  currentEligibility,
  initialEligibility,
} = {}) => {
  const reviewStage = normalizeWorkflowStage(
    reviewWorkflow?.currentStage ?? reviewWorkflow?.current_stage
  );
  const current = normalizeEligibility(currentEligibility);
  const initial = normalizeEligibility(initialEligibility);
  return Boolean(
    isSubmitterCorrectionStage(reviewStage) &&
    current &&
    current === initial
  );
};

export const isReturnedAssessmentEligibilityChangeUnverified = ({
  reviewWorkflow,
  currentEligibility,
  initialEligibility,
  hasVerificationDocument = false,
  hasSelectedVerificationFile = false,
} = {}) => {
  const reviewStage = normalizeWorkflowStage(
    reviewWorkflow?.currentStage ?? reviewWorkflow?.current_stage
  );
  const current = normalizeEligibility(currentEligibility);
  const initial = normalizeEligibility(initialEligibility);
  return Boolean(
    isSubmitterCorrectionStage(reviewStage) &&
    current !== initial &&
    !hasVerificationDocument &&
    !hasSelectedVerificationFile
  );
};

export const isCurrentApplicationAssessmentWorkflowSubmitter = ({
  reviewWorkflow,
  currentStaffProfileId,
} = {}) => {
  const submitterStaffProfileId = normalizePositiveId(
    reviewWorkflow?.submittedByStaffProfileId ??
    reviewWorkflow?.submitted_by_staff_profile_id
  );
  const activeStaffProfileId = normalizePositiveId(currentStaffProfileId);
  return Boolean(
    submitterStaffProfileId &&
    activeStaffProfileId &&
    submitterStaffProfileId === activeStaffProfileId
  );
};

const isCurrentAssessmentCorrectionOwner = ({ reviewWorkflow, assignedStaffProfileId, currentStaffProfileId }) =>
  canPerformStaffWork({
    actorStaffProfileId: currentStaffProfileId,
    assignedStaffProfileId,
    historicalStaffProfileId: reviewWorkflow?.submittedByStaffProfileId ?? reviewWorkflow?.submitted_by_staff_profile_id,
  });

export const canRegionalManagerEditApplicationAssessment = ({
  isRegionalManager,
  applicationStatus,
  reviewWorkflow,
  currentStaffProfileId,
  assignedStaffProfileId,
} = {}) => {
  const normalizedApplicationStatus = String(applicationStatus || '').trim().toLowerCase();
  // Waiting for applicant input does not suspend independent staff drafting
  // (WP-01-D1). Submitted review workflows still control who may edit below.
  const draftStatuses = ['submitted', 'in_review', 'docs_requested', 'awaiting_applicant', 'closure_notice', 'on_hold'];
  if (!isRegionalManager || !draftStatuses.includes(normalizedApplicationStatus)) {
    return false;
  }

  if (!reviewWorkflow) {
    if (normalizedApplicationStatus === 'submitted') {
      const activeStaffProfileId = normalizePositiveId(currentStaffProfileId);
      const assignedProfileId = normalizePositiveId(assignedStaffProfileId);
      return Boolean(
        activeStaffProfileId &&
        assignedProfileId &&
        activeStaffProfileId === assignedProfileId
      );
    }
    return true;
  }

  const reviewStage = normalizeWorkflowStage(
    reviewWorkflow.currentStage ?? reviewWorkflow.current_stage
  );
  if (!isSubmitterCorrectionStage(reviewStage)) {
    return false;
  }

  return isCurrentAssessmentCorrectionOwner({
    reviewWorkflow,
    assignedStaffProfileId,
    currentStaffProfileId,
  });
};

export const canEditApplicationAssessmentBody = ({
  isAssessor,
  isRegionalManager,
  isSystemAdministrator,
  applicationStatus,
  reviewWorkflow,
  currentStaffProfileId,
  assignedStaffProfileId,
} = {}) => {
  if (isSystemAdministrator) {
    return true;
  }

  const reviewStage = normalizeWorkflowStage(
    reviewWorkflow?.currentStage ?? reviewWorkflow?.current_stage
  );
  if (isSubmitterCorrectionStage(reviewStage)) {
    return Boolean(
      (isAssessor || isRegionalManager) &&
      isCurrentAssessmentCorrectionOwner({
        reviewWorkflow,
        assignedStaffProfileId,
        currentStaffProfileId,
      })
    );
  }

  if (isAssessor) {
    return true;
  }

  return canRegionalManagerEditApplicationAssessment({
    isRegionalManager,
    applicationStatus,
    reviewWorkflow,
    currentStaffProfileId,
    assignedStaffProfileId,
  });
};
